import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Send } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime, setPageTitle } from "../lib/format";

export default function SupportTicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const endRef = useRef(null);

  const load = () => api.getTicket(id).then(setTicket).catch((e) => setError(e.message));
  useEffect(() => { setPageTitle("Support Ticket"); load(); }, [id]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [ticket?.messages?.length]);

  const reply = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    try {
      await api.replyTicket(id, draft.trim());
      setDraft("");
      await load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="ticket-detail-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <Link to="/support" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="back-to-support">
          <ChevronLeft className="w-4 h-4" /> Support
        </Link>
        {error ? <div className="mt-8"><ErrorState body={error} onRetry={load} /></div> : !ticket ? <PageLoading label="Loading ticket…" /> : (
          <div className="mt-6 card-dark p-6" data-testid="ticket-detail">
            <div className="flex flex-wrap items-start justify-between gap-3 pb-5 border-b border-[#27272A]">
              <div>
                <h1 className="text-xl font-bold" data-testid="ticket-subject">{ticket.subject}</h1>
                <p className="text-xs text-zinc-500 mt-1">{ticket.category} · opened {fmtDateTime(ticket.created_at)}</p>
              </div>
              <div className="flex gap-2"><StatusBadge value={ticket.priority} /><StatusBadge value={ticket.status} /></div>
            </div>
            <div className="py-5 space-y-3 max-h-[28rem] overflow-auto" data-testid="ticket-messages" aria-live="polite">
              {(ticket.messages || []).map((m) => (
                <div key={m.message_id} className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.sender_id === user?.user_id ? "ml-auto bg-[#E50914] text-white" : "bg-[#080B0D] border border-[#27272A]"}`}>
                  <p className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {fmtDateTime(m.created_at)}</p>{m.text}
                </div>
              ))}
              <div ref={endRef} />
            </div>
            {!["resolved", "closed"].includes(ticket.status) ? (
              <form onSubmit={reply} className="flex gap-2 pt-4 border-t border-[#27272A]">
                <input data-testid="ticket-reply-input" value={draft} onChange={(e) => setDraft(e.target.value)} className="input-dark flex-1" placeholder="Write a reply…" aria-label="Reply" />
                <button type="submit" data-testid="ticket-reply-btn" className="btn-primary !px-4" aria-label="Send reply"><Send className="w-4 h-4" /></button>
              </form>
            ) : (
              <p className="pt-4 border-t border-[#27272A] text-sm text-zinc-500">This ticket is {ticket.status}.{ticket.resolution ? ` Resolution: ${ticket.resolution}` : ""}</p>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
